import './style.scss';

import React, { memo, useCallback, useMemo, useState } from 'react';
import Creatable from 'react-select/creatable';
import _ from 'lodash';
import { DARK_INPUT_STYLE } from 'app/ReactSelectStyles';
import { useQuery } from '@tanstack/react-query';
import { getAttendingVeterinariansQueryOptions } from 'app/queries/attendingVeterinariansQueries';
import { OptionProps, components } from 'react-select';
import AttendingVeterinarianDeleteButton from 'app/components/AttendingVeterinarianSelector/AttendingVeterinarianDeleteButton';
import { useIntl } from 'react-intl';

type Option = {
  label: string;
  value: string;
};

const OptionWithDelete = (props: OptionProps<Option, false>) => {
  if (props.data.__isNew__) {
    return <components.Option {...props} />;
  }
  return (
    <components.Option {...props}>
      <AttendingVeterinarianDeleteButton name={props.data.value}>
        {props.children}
      </AttendingVeterinarianDeleteButton>
    </components.Option>
  );
};

const entryToOption = (entry: string) => ({
  label: entry,
  value: entry,
});

type AttendingVeterinarianSelectorProps = {
  attendingVeterinarian?: string;
  onChange: (name: string | undefined) => void;
};

function AttendingVeterinarianSelector({
  attendingVeterinarian,
  onChange,
}: AttendingVeterinarianSelectorProps) {
  const intl = useIntl();
  const { data: attendingVeterinarians, isLoading } = useQuery(
    getAttendingVeterinariansQueryOptions()
  );
  const [createdAttendingVeterinarians, setCreatedAttendingVeterinarians] = useState<string[]>([]);

  const attendingVeterinarianOptions = useMemo(
    () =>
      [...(attendingVeterinarians ?? []), ...createdAttendingVeterinarians]
        .toSorted()
        .map(entryToOption) ?? [],
    [attendingVeterinarians, createdAttendingVeterinarians]
  );
  const attendingVeterinarianOption = useMemo(
    () => attendingVeterinarian && entryToOption(attendingVeterinarian),
    [attendingVeterinarian]
  );

  const onCreateAttendingVeterinarian = useCallback(
    (name: string) => {
      if (_.isEmpty(name)) return;
      const existingAttendingVeterinarian = attendingVeterinarianOptions.find(
        (option) => option.value.toLowerCase() === name.toLowerCase()
      );
      if (existingAttendingVeterinarian) {
        onChange(existingAttendingVeterinarian.value);
        return;
      }
      setCreatedAttendingVeterinarians((prevState) => [...prevState, name]);
      onChange(name);
    },
    [setCreatedAttendingVeterinarians, onChange]
  );

  return (
    <Creatable
      onChange={(value) => onChange(value?.value)}
      options={attendingVeterinarianOptions}
      value={attendingVeterinarianOption}
      onCreateOption={onCreateAttendingVeterinarian}
      components={{ Option: OptionWithDelete }}
      styles={DARK_INPUT_STYLE}
      isClearable
      isLoading={isLoading}
      loadingMessage={() => intl.formatMessage({ id: 'general.loading' })}
      placeholder=""
      formatCreateLabel={(inputValue) =>
        `${intl.formatMessage({ id: 'general.add' })} "${inputValue}"`
      }
    />
  );
}

export default memo(AttendingVeterinarianSelector);
